import React, { useCallback, useContext, useEffect, useState } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useSpecificPayroll from '@ubeya/shared/hooks/account/useSpecificPayroll';
import { EditableInputRenderer, Tooltip } from '@ubeya/shared-web/components';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { isReadOnly } from 'shared/utils/timesheet';

const EditableWageRenderer = (params) => {
  const {
    rowData,
    rowData: { summarized, subRows, timesheetId, employee },
    columnData: {
      refetchPayroll,
      clientWageCase = false,
      updateClientRate,
      updateWageValues,
      parent = 'wage',
      wageKey,
      withPrefix,
      isEditable
    }
  } = params;
  const { currency, t } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);

  const hasAmendPayRatePrivilege = !!myPrivileges[privileges.AMEND_PAY_RATE];
  const fieldValue = rowData?.[parent]?.[wageKey] ?? 0;
  const fieldValueOrigin = rowData?.[parent]?.type;

  const [value, setValue] = useState(fieldValue);
  const [prevValue, setPrevValue] = useState(value);

  useEffect(() => {
    setValue(fieldValue);
  }, [fieldValue]);

  const {
    updateWage: { mutateAsync: update, isLoading }
  } = useSpecificPayroll(refetchPayroll);

  const updateValue = useCallback(
    async (e) => {
      // when 'enter' on value if fires this both because of blur after
      if (!e) {
        return;
      }
      if (prevValue !== value) {
        //payroll update
        try {
          const { id, hourlyWage, flatWage, flatHours } = rowData?.[parent] || {};
          const newWage = { hourlyWage, flatWage, flatHours, [wageKey]: value || 0 };

          await update({
            wageId: id,
            ...newWage,
            timesheetId,
            employeeId: employee?.id
          });

          if (clientWageCase) {
            updateClientRate?.({ slotId: rowData?.id, shiftId: rowData?.shiftId, hourlyWage: value });
          } else {
            updateWageValues?.({
              slotId: rowData?.id,
              shiftId: rowData?.shiftId,
              key: wageKey,
              value,
              isLeave: rowData?.isLeave
            });
          }

          // local component state update
          setPrevValue(value);
        } catch (err) {
          // revert value on error
          setValue(prevValue);
        }
      }
    },
    [
      prevValue,
      value,
      update,
      rowData,
      parent,
      wageKey,
      timesheetId,
      employee?.id,
      clientWageCase,
      updateClientRate,
      updateWageValues
    ]
  );

  if (subRows || summarized || !rowData?.[parent]?.id) {
    return <></>;
  }

  const tooltipBody = fieldValueOrigin ? t(`wage-tooltip-${fieldValueOrigin}`) : undefined;

  const isEditableLeave =
    rowData?.isLeave &&
    rowData?.leaveSupportsManualRate &&
    wageKey === 'hourlyWage' &&
    !clientWageCase &&
    !rowData?.approved &&
    !rowData?.isLocked &&
    !rowData?.payrollProcess;
  const isRowReadOnly = rowData?.isLeave ? !isEditableLeave : isReadOnly(rowData);

  if (!isEditable || isRowReadOnly || !hasAmendPayRatePrivilege) {
    const isPto = !!rowData?.ptoType;
    const readOnlyValue = isPto ? rowData?.['payroll']?.[parent]?.[wageKey] ?? value : value;
    return (
      <Tooltip body={tooltipBody} disabled={!tooltipBody}>
        <div>
          {withPrefix && currency}
          {readOnlyValue}
        </div>
      </Tooltip>
    );
  }

  return (
    <EditableInputRenderer
      isLoading={isLoading}
      value={value}
      onChange={setValue}
      onSubmitChange={updateValue}
      prefix={value !== '' ? withPrefix && currency : ''}
      tooltip={tooltipBody}
      fillWidth
    />
  );
};

export default EditableWageRenderer;
